import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  getScreens,
  uploadScreen,
  getHotspots,
  createHotspot,
  updateHotspot,
  deleteHotspot,
  BASE_URL,
} from "../api";
import { useTheme } from "../ThemeContext";
import ConfirmDialog from "../components/ConfirmDialog";
import { useToast } from "../components/Toast";

const HANDLES = ["nw", "n", "ne", "e", "se", "s", "sw", "w"];
const handleCursors = {
  nw: "nw-resize",
  n: "n-resize",
  ne: "ne-resize",
  e: "e-resize",
  se: "se-resize",
  s: "s-resize",
  sw: "sw-resize",
  w: "w-resize",
};

export default function HotspotEditor() {
  const { colors, common, mode } = useTheme();
  const navigate = useNavigate();
  const toast = useToast();
  const projectId = window.location.pathname.split("/")[2];
  const screenId = window.location.pathname.split("/")[4];

  const [screens, setScreens] = useState([]);
  const [currentScreen, setCurrentScreen] = useState(null);
  const [hotspots, setHotspots] = useState([]);
  const [drawing, setDrawing] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [currentRect, setCurrentRect] = useState(null);
  const [selectedHotspot, setSelectedHotspot] = useState(null);
  const [showPanel, setShowPanel] = useState(false);
  const [tooltipText, setTooltipText] = useState("");
  const [tooltipPosition, setTooltipPosition] = useState("top");
  const [successMessage, setSuccessMessage] = useState("");
  const [failMessage, setFailMessage] = useState("");
  const [hotspotType, setHotspotType] = useState("click");
  const [correctAnswer, setCorrectAnswer] = useState("");
  const [requiresPrevious, setRequiresPrevious] = useState(false);
  const [hotspotShape, setHotspotShape] = useState("rectangle");
  const [roundedRadius, setRoundedRadius] = useState(12);
  const [stepNumber, setStepNumber] = useState(1);
  const [rightClick, setRightClick] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [clipboardHotspot, setClipboardHotspot] = useState(() => {
    try { return JSON.parse(localStorage.getItem('simbuilder_hotspot_clipboard')); } catch { return null; }
  });

  const interactRef = useRef(null);
  const imageRef = useRef(null);

  // Always-current refs so keyboard handlers (registered once) never have stale closures
  const selectedHotspotRef = useRef(null);
  selectedHotspotRef.current = selectedHotspot;
  const hotspotsRef = useRef([]);
  hotspotsRef.current = hotspots;
  // Next free step number on the current screen (one past the current max)
  const nextStepNumber = () =>
    hotspotsRef.current.reduce((m, h) => Math.max(m, h.step_number || 1), 0) + 1;
  const fieldStateRef = useRef({});
  fieldStateRef.current = { tooltipText, tooltipPosition, successMessage, failMessage, hotspotType, correctAnswer, requiresPrevious, hotspotShape, roundedRadius, stepNumber, rightClick };
  const screenIdRef = useRef(screenId);
  screenIdRef.current = screenId;

  // ── Realtime autosave ───────────────────────────────────────────────────────
  // Any change to the selected hotspot (move, resize, nudge, or a panel field)
  // is persisted automatically after a short debounce, so nothing relies on a
  // Save button. A pending save is flushed immediately when the selection
  // changes or the editor unmounts, so no edit is ever lost.
  const autosaveTimer = useRef(null);
  const pendingSave = useRef(null); // { id, payload }
  const lastSavedSig = useRef("");

  const flushHotspotSave = () => {
    if (autosaveTimer.current) { clearTimeout(autosaveTimer.current); autosaveTimer.current = null; }
    const p = pendingSave.current;
    if (!p) return;
    pendingSave.current = null;
    updateHotspot(p.id, p.payload)
      .then(() => setHotspots((prev) => prev.map((h) => (h.id === p.id ? { ...h, ...p.payload } : h))))
      .catch(() => {});
  };

  // Signature of a hotspot's saved state — must match the autosave payload's key
  // order exactly so a freshly-loaded hotspot isn't re-saved needlessly.
  const sigForHotspot = (h) => JSON.stringify([h.id, {
    x: h.x, y: h.y, width: h.width, height: h.height,
    tooltip_text: h.tooltip_text || "", tooltip_position: h.tooltip_position || "top",
    success_message: h.success_message || "", fail_message: h.fail_message || "",
    type: h.type || "click", correct_answer: h.correct_answer || "",
    requires_previous: h.requires_previous || false, shape: h.shape || "rectangle",
    border_radius: h.border_radius ?? 12, step_number: h.step_number ?? 1, right_click: !!h.right_click,
  }]);

  useEffect(() => {
    fetchData();
  }, [screenId]);

  // Debounced autosave whenever the selected hotspot's geometry or fields change
  useEffect(() => {
    if (!selectedHotspot) return;
    const hs = hotspotsRef.current.find((h) => h.id === selectedHotspot.id);
    if (!hs) return;
    const payload = {
      x: hs.x, y: hs.y, width: hs.width, height: hs.height,
      tooltip_text: tooltipText, tooltip_position: tooltipPosition,
      success_message: successMessage, fail_message: failMessage,
      type: hotspotType, correct_answer: correctAnswer,
      requires_previous: requiresPrevious, shape: hotspotShape,
      border_radius: roundedRadius, step_number: stepNumber, right_click: rightClick,
    };
    const sig = JSON.stringify([selectedHotspot.id, payload]);
    if (sig === lastSavedSig.current) return;
    pendingSave.current = { id: selectedHotspot.id, payload };
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      autosaveTimer.current = null;
      pendingSave.current = null;
      lastSavedSig.current = sig;
      updateHotspot(selectedHotspot.id, payload)
        .then(() => setHotspots((prev) => prev.map((h) => (h.id === selectedHotspot.id ? { ...h, ...payload } : h))))
        .catch(() => {});
    }, 400);
    return () => { if (autosaveTimer.current) clearTimeout(autosaveTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHotspot, hotspots, tooltipText, tooltipPosition, successMessage, failMessage, hotspotType, correctAnswer, requiresPrevious, hotspotShape, roundedRadius, stepNumber, rightClick]);

  // Flush any pending save when the editor unmounts
  useEffect(() => () => flushHotspotSave(), []);

  // ── Copy a hotspot to localStorage clipboard ────────────────────────────────
  const handleCopyHotspot = () => {
    const hs = selectedHotspotRef.current;
    const f = fieldStateRef.current;
    if (!hs) return;
    const payload = {
      x: hs.x, y: hs.y, width: hs.width, height: hs.height,
      tooltip_text: f.tooltipText, tooltip_position: f.tooltipPosition,
      success_message: f.successMessage, fail_message: f.failMessage,
      type: f.hotspotType, correct_answer: f.correctAnswer,
      shape: f.hotspotShape, border_radius: f.roundedRadius,
      right_click: f.rightClick,
      // requires_previous and step_number intentionally omitted — ordering
      // context won't carry over; the paste assigns a fresh step
    };
    localStorage.setItem('simbuilder_hotspot_clipboard', JSON.stringify(payload));
    setClipboardHotspot(payload);
    toast('Hotspot copied — press Ctrl+V on any screen to paste.', 'success');
  };

  // ── Paste clipboard hotspot onto the current screen ─────────────────────────
  const handlePasteHotspot = async () => {
    let payload;
    try { payload = JSON.parse(localStorage.getItem('simbuilder_hotspot_clipboard')); } catch { return; }
    if (!payload) return;
    try {
      const { data } = await createHotspot(screenIdRef.current, {
        ...payload,
        // Offset the pasted copy slightly (2% of the image) so it's visible
        x: clamp01(payload.x + 0.02),
        y: clamp01(payload.y + 0.02),
        requires_previous: false,
        step_number: nextStepNumber(),
      });
      setHotspots((prev) => [...prev, data]);
      setSelectedHotspot(data);
      setTooltipText(data.tooltip_text || '');
      setTooltipPosition(data.tooltip_position || 'top');
      setSuccessMessage(data.success_message || '');
      setFailMessage(data.fail_message || '');
      setHotspotType(data.type || 'click');
      setCorrectAnswer(data.correct_answer || '');
      setRequiresPrevious(false);
      setHotspotShape(data.shape || 'rectangle');
      setRoundedRadius(data.border_radius ?? 12);
      setStepNumber(data.step_number ?? 1);
      setRightClick(!!data.right_click);
      lastSavedSig.current = sigForHotspot(data);
      setShowPanel(true);
      toast('Hotspot pasted.', 'success');
    } catch {
      toast('Could not paste hotspot.', 'error');
    }
  };

  // Arrow keys (1px nudge, Shift = 10px) + Ctrl+C / Ctrl+V
  useEffect(() => {
    const handler = (e) => {
      const inTextField = ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName);

      // Ctrl+C — copy selected hotspot
      if ((e.ctrlKey || e.metaKey) && e.key === 'c' && !inTextField) {
        if (selectedHotspotRef.current) { e.preventDefault(); handleCopyHotspot(); }
        return;
      }

      // Ctrl+V — paste
      if ((e.ctrlKey || e.metaKey) && e.key === 'v' && !inTextField) {
        e.preventDefault();
        handlePasteHotspot();
        return;
      }

      // Arrow key nudge
      const hs = selectedHotspotRef.current;
      if (!hs || inTextField) return;
      const delta = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[e.key];
      if (!delta) return;
      e.preventDefault();
      // Nudge by whole screen pixels, converted to fractions of the image
      const stepPx = e.shiftKey ? 10 : 1;
      const rect = imageRef.current?.getBoundingClientRect();
      if (!rect || !rect.width || !rect.height) return;
      const dx = delta[0] * (stepPx / rect.width);
      const dy = delta[1] * (stepPx / rect.height);
      // State update triggers the realtime autosave; no explicit save here.
      setSelectedHotspot((prev) => prev ? { ...prev, x: clamp01(prev.x + dx), y: clamp01(prev.y + dy) } : prev);
      setHotspots((prev) => prev.map((h) => h.id === hs.id ? { ...h, x: clamp01(h.x + dx), y: clamp01(h.y + dy) } : h));
    };
    document.addEventListener('keydown', handler);
    return () => { document.removeEventListener('keydown', handler); };
  }, []);

  const fetchData = async () => {
    try {
      const { data: scr } = await getScreens(projectId);
      setScreens(scr);
      setCurrentScreen(scr.find((s) => s.id === parseInt(screenId)));
      const { data: hs } = await getHotspots(screenId);
      setHotspots(hs);
    } catch (err) {
      console.error("Could not load data");
    }
  };

  // Coordinates are stored as fractions of the image (0-1). Mouse position is
  // converted to a fraction using the image's rendered rect — read inside event
  // handlers only (post-layout), so it is reliable regardless of load timing.
  const clamp01 = (n) => Math.min(1, Math.max(0, n));

  const getImagePos = (e) => {
    const img = imageRef.current;
    const rect = img.getBoundingClientRect();
    if (!rect.width || !rect.height) return { x: 0, y: 0 };
    return {
      x: clamp01((e.clientX - rect.left) / rect.width),
      y: clamp01((e.clientY - rect.top) / rect.height),
    };
  };

  // Fraction of the image per screen pixel — used to turn drag deltas (in px)
  // into coordinate deltas (in fractions).
  const getScale = () => {
    const img = imageRef.current;
    if (!img) return { scaleX: 0, scaleY: 0 };
    const rect = img.getBoundingClientRect();
    if (!rect.width || !rect.height) return { scaleX: 0, scaleY: 0 };
    return { scaleX: 1 / rect.width, scaleY: 1 / rect.height };
  };

  const MIN_FRAC = 0.01; // smallest hotspot size (~1% of the image)

  const handleImageMouseDown = (e) => {
    if (e.target !== imageRef.current) return;
    flushHotspotSave();
    setSelectedHotspot(null);
    setShowPanel(false);
    const pos = getImagePos(e);
    setStartPos({ x: pos.x, y: pos.y });
    setDrawing(true);
    setCurrentRect({ x: pos.x, y: pos.y, width: 0, height: 0 });
  };

  const handleMouseMove = (e) => {
    if (interactRef.current?.mode === "resize") {
      const { handle, startMouseX, startMouseY, origX, origY, origW, origH } =
        interactRef.current;
      const { scaleX, scaleY } = getScale();
      const dx = (e.clientX - startMouseX) * scaleX;
      const dy = (e.clientY - startMouseY) * scaleY;
      let x = origX,
        y = origY,
        w = origW,
        h = origH;
      if (handle.includes("e")) w = Math.max(MIN_FRAC, origW + dx);
      if (handle.includes("s")) h = Math.max(MIN_FRAC, origH + dy);
      if (handle.includes("w")) {
        x = origX + dx;
        w = Math.max(MIN_FRAC, origW - dx);
      }
      if (handle.includes("n")) {
        y = origY + dy;
        h = Math.max(MIN_FRAC, origH - dy);
      }
      const updated = {
        x: clamp01(x),
        y: clamp01(y),
        width: w,
        height: h,
      };
      setHotspots((prev) =>
        prev.map((h2) =>
          h2.id === interactRef.current.hotspotId ? { ...h2, ...updated } : h2,
        ),
      );
      setSelectedHotspot((prev) => (prev ? { ...prev, ...updated } : prev));
      return;
    }
    if (interactRef.current?.mode === "move") {
      const { startMouseX, startMouseY, origX, origY } = interactRef.current;
      const { scaleX, scaleY } = getScale();
      const newX = clamp01(origX + (e.clientX - startMouseX) * scaleX);
      const newY = clamp01(origY + (e.clientY - startMouseY) * scaleY);
      setHotspots((prev) =>
        prev.map((h2) =>
          h2.id === interactRef.current.hotspotId
            ? { ...h2, x: newX, y: newY }
            : h2,
        ),
      );
      setSelectedHotspot((prev) =>
        prev ? { ...prev, x: newX, y: newY } : prev,
      );
      return;
    }
    if (!drawing) return;
    const pos = getImagePos(e);
    setCurrentRect({
      x: Math.min(pos.x, startPos.x),
      y: Math.min(pos.y, startPos.y),
      width: Math.abs(pos.x - startPos.x),
      height: Math.abs(pos.y - startPos.y),
    });
  };

  const handleMouseUp = async () => {
    if (
      interactRef.current?.mode === "resize" ||
      interactRef.current?.mode === "move"
    ) {
      // Position/size already updated in state during the drag; the realtime
      // autosave persists it (no explicit save needed here).
      interactRef.current = null;
      return;
    }
    if (!drawing) return;
    setDrawing(false);
    if (!currentRect || currentRect.width < MIN_FRAC || currentRect.height < MIN_FRAC) {
      setCurrentRect(null);
      return;
    }
    try {
      const { data } = await createHotspot(screenId, {
        x: currentRect.x,
        y: currentRect.y,
        width: currentRect.width,
        height: currentRect.height,
        tooltip_text: "",
        tooltip_position: "top",
        success_message: "",
        fail_message: "",
        type: "click",
        correct_answer: "",
        requires_previous: false,
        shape: "rectangle",
        border_radius: 12,
        step_number: nextStepNumber(),
        right_click: false,
      });
      setHotspots((prev) => [...prev, data]);
      setSelectedHotspot(data);
      setTooltipText("");
      setTooltipPosition("top");
      setSuccessMessage("");
      setFailMessage("");
      setHotspotType("click");
      setCorrectAnswer("");
      setRequiresPrevious(false);
      setHotspotShape("rectangle");
      setRoundedRadius(12);
      setStepNumber(data.step_number ?? 1);
      setRightClick(false);
      lastSavedSig.current = sigForHotspot(data);
      setShowPanel(true);
    } catch (err) {
      console.error("Could not create hotspot");
    }
    setCurrentRect(null);
  };

  const handleHotspotMouseDown = (e, hotspot) => {
    e.stopPropagation();
    // Persist any pending edit to the previously selected hotspot first
    flushHotspotSave();
    setSelectedHotspot(hotspot);
    setTooltipText(hotspot.tooltip_text || "");
    setTooltipPosition(hotspot.tooltip_position || "top");
    setSuccessMessage(hotspot.success_message || "");
    setFailMessage(hotspot.fail_message || "");
    setHotspotType(hotspot.type || "click");
    setCorrectAnswer(hotspot.correct_answer || "");
    setRequiresPrevious(hotspot.requires_previous || false);
    setHotspotShape(hotspot.shape || "rectangle");
    setRoundedRadius(hotspot.border_radius ?? 12);
    setStepNumber(hotspot.step_number ?? 1);
    setRightClick(!!hotspot.right_click);
    lastSavedSig.current = sigForHotspot(hotspot);
    setShowPanel(true);
    interactRef.current = {
      mode: "move",
      hotspotId: hotspot.id,
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      origX: hotspot.x,
      origY: hotspot.y,
    };
  };

  const handleResizeMouseDown = (e, hotspot, handle) => {
    e.stopPropagation();
    interactRef.current = {
      mode: "resize",
      handle,
      hotspotId: hotspot.id,
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      origX: hotspot.x,
      origY: hotspot.y,
      origW: hotspot.width,
      origH: hotspot.height,
    };
  };

  // Changes autosave continuously; "Done" just flushes any pending save and closes.
  const handleSaveHotspot = () => {
    flushHotspotSave();
    setShowPanel(false);
    setSelectedHotspot(null);
  };

  // Clicking blank canvas space (not a hotspot or the image) acts like "Done"
  const handleBlankClick = (e) => {
    if (e.target !== e.currentTarget) return;
    if (selectedHotspot || showPanel) handleSaveHotspot();
  };

  const handleDeleteHotspot = () => {
    setConfirmDelete(true);
  };

  const doDeleteHotspot = async () => {
    setConfirmDelete(false);
    // Cancel any queued autosave for the hotspot we're deleting
    if (autosaveTimer.current) { clearTimeout(autosaveTimer.current); autosaveTimer.current = null; }
    pendingSave.current = null;
    await deleteHotspot(selectedHotspot.id);
    setHotspots((prev) => prev.filter((h) => h.id !== selectedHotspot.id));
    setShowPanel(false);
    setSelectedHotspot(null);
  };

  // Coordinates are fractions (0-1) of the image, so positions are pure CSS
  // percentages relative to the image container. No measurement needed, so this
  // is correct at any screen size and never depends on image load timing.
  const pct = (n) => `${n * 100}%`;

  const getScaledStyle = (hotspot) => ({
    left: pct(hotspot.x),
    top: pct(hotspot.y),
    width: pct(hotspot.width),
    height: pct(hotspot.height),
  });

  const getCurrentRectStyle = () => {
    if (!currentRect) return { display: "none" };
    return {
      left: pct(currentRect.x),
      top: pct(currentRect.y),
      width: pct(currentRect.width),
      height: pct(currentRect.height),
    };
  };

  const handleSidebarUpload = async (e) => {
    try {
      for (const file of Array.from(e.target.files)) {
        const formData = new FormData();
        formData.append("image", file);
        await uploadScreen(projectId, formData);
      }
      fetchData();
    } catch (err) {
      console.error("Upload failed");
    }
  };

  const handleSidebarPaste = async () => {
    try {
      const clipboardItems = await navigator.clipboard.read();
      let foundImage = false;
      for (const item of clipboardItems) {
        const imageType = item.types.find((t) => t.startsWith("image/"));
        if (imageType) {
          foundImage = true;
          const blob = await item.getType(imageType);
          const file = new File([blob], `paste-${Date.now()}.png`, {
            type: imageType,
          });
          const formData = new FormData();
          formData.append("image", file);
          await uploadScreen(projectId, formData);
          fetchData();
        }
      }
      if (!foundImage) toast("No image found in clipboard. Please copy an image first.", "error");
    } catch (err) {
      toast("Could not access clipboard. Please make sure you have copied an image.", "error");
    }
  };

  const getHotspotOrder = (hotspot) => hotspots.findIndex((h) => h.id === hotspot.id) + 1;

  const shapeBorderRadius = (shape, radius = 12) => {
    if (shape === "circle") return "50%";
    if (shape === "rounded") return radius;
    return 2;
  };

  // ── Styles computed inside component so they react to theme changes ────────
  const s = {
    container: {
      minHeight: "100vh",
      backgroundColor: colors.pageBg,
      display: "flex",
      flexDirection: "column",
      userSelect: "none",
      color: colors.textPrimary,
      fontFamily: "system-ui, sans-serif",
    },
    hint: { margin: 0, color: mode === 'dark' ? 'rgba(255,255,255,0.85)' : '#1e293b', fontSize: 13 },
    main: {
      display: "flex",
      flex: 1,
      overflow: "hidden",
      minHeight: 0,
      position: "relative",
    },
    sidebar: {
      width: 160,
      backgroundColor: colors.headerBg,
      padding: 16,
      overflowY: "auto",
      borderRight: `1px solid ${colors.borderColor}`,
      flexShrink: 0,
    },
    sidebarTitle: {
      color: colors.textPrimary,
      margin: "0 0 12px 0",
      fontSize: 14,
      fontWeight: 600,
    },
    sidebarBtn: {
      display: "block",
      width: "100%",
      marginBottom: 6,
      justifyContent: "center",
      fontSize: 12,
      padding: "6px 8px",
    },
    screenThumb: {
      borderRadius: 6,
      overflow: "hidden",
      marginBottom: 12,
      cursor: "pointer",
    },
    thumbImage: { width: "100%", display: "block" },
    thumbLabel: {
      display: "block",
      color: colors.textMuted,
      fontSize: 12,
      padding: 4,
      textAlign: "center",
      backgroundColor: colors.headerBg,
    },
    canvas: {
      flex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 24,
      overflow: "auto",
      minWidth: 0,
      minHeight: 0,
    },
    imageContainer: {
      position: "relative",
      display: "inline-block",
      lineHeight: 0,
      maxWidth: "100%",
    },
    image: {
      display: "block",
      maxWidth: "100%",
      maxHeight: "calc(100vh - 100px)",
      objectFit: "contain",
      userSelect: "none",
      cursor: "crosshair",
    },
    hotspot: { position: "absolute", borderRadius: 2 },
    panel: {
      position: "absolute",
      top: 0,
      right: 0,
      bottom: 0,
      width: 260,
      backgroundColor: colors.headerBg,
      padding: 20,
      overflowY: "auto",
      borderLeft: `1px solid ${colors.borderColor}`,
      zIndex: 10,
      boxShadow: "-4px 0 16px rgba(0,0,0,0.3)",
    },
    panelTitle: {
      color: colors.textPrimary,
      margin: "0 0 12px 0",
      fontWeight: 700,
      fontSize: 15,
    },
    orderBadge: {
      display: "inline-block",
      backgroundColor: colors.indigoSubtle,
      color: "#a5b4fc",
      fontSize: 12,
      fontWeight: 600,
      padding: "3px 10px",
      borderRadius: 20,
      border: "1px solid rgba(99,102,241,0.35)",
      marginBottom: 10,
    },
    coordsRow: { display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 },
    coordChip: {
      backgroundColor: colors.hoverBg,
      color: colors.textSecondary,
      fontSize: 11,
      padding: "2px 8px",
      borderRadius: 4,
      border: `1px solid ${colors.borderColor}`,
      fontFamily: "monospace",
    },
    panelInput: {
      width: "100%",
      padding: "8px 10px",
      marginBottom: 14,
      borderRadius: 6,
      border: `1px solid ${colors.borderColor}`,
      backgroundColor: colors.inputBg,
      color: colors.textPrimary,
      fontSize: 13,
      boxSizing: "border-box",
    },
    panelTextarea: {
      width: "100%",
      padding: "8px 10px",
      marginBottom: 14,
      borderRadius: 6,
      border: `1px solid ${colors.borderColor}`,
      backgroundColor: colors.inputBg,
      color: colors.textPrimary,
      fontSize: 13,
      boxSizing: "border-box",
      resize: "vertical",
    },
    select: {
      width: "100%",
      padding: "8px 10px",
      marginBottom: 14,
      borderRadius: 6,
      border: `1px solid ${colors.borderColor}`,
      backgroundColor: colors.inputBg,
      color: colors.textPrimary,
      fontSize: 13,
      boxSizing: "border-box",
    },
    checkboxRow: {
      display: "flex",
      alignItems: "flex-start",
      gap: 10,
      backgroundColor: "rgba(245,158,11,0.08)",
      border: "1px solid rgba(245,158,11,0.25)",
      borderRadius: 8,
      padding: "10px 12px",
      marginBottom: 14,
      cursor: "pointer",
    },
    checkbox: {
      marginTop: 2,
      accentColor: colors.amber,
      width: 16,
      height: 16,
      flexShrink: 0,
      cursor: "pointer",
    },
    checkboxLabel: {
      color: colors.textPrimary,
      fontSize: 13,
      fontWeight: 500,
      cursor: "pointer",
      display: "flex",
      flexDirection: "column",
      gap: 4,
    },
    checkboxHint: {
      color: colors.textMuted,
      fontSize: 11,
      fontWeight: 400,
      lineHeight: 1.4,
    },
  };

  // Resize handle styles also need colors
  const getHandleStyle = (handle) => {
    const size = 8;
    const offset = -(size / 2);
    const mid = "calc(50% - 4px)";
    const base = {
      position: "absolute",
      width: size,
      height: size,
      backgroundColor: colors.amber,
      border: "1px solid white",
      borderRadius: 2,
      zIndex: 10,
    };
    const positions = {
      nw: { top: offset, left: offset },
      n: { top: offset, left: mid },
      ne: { top: offset, right: offset },
      e: { top: mid, right: offset },
      se: { bottom: offset, right: offset },
      s: { bottom: offset, left: mid },
      sw: { bottom: offset, left: offset },
      w: { top: mid, left: offset },
    };
    return { ...base, ...positions[handle] };
  };

  return (
    <div
      style={s.container}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      {/* ── Header ── */}
      <header style={common.header}>
        <div style={common.headerLeft}>
          <a href="/dashboard" aria-label="Back to dashboard" style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <img src={mode === 'dark' ? '/Logo-Dark.png' : '/Logo-Light.png'} alt="Software Simulation Builder logo" style={{ height: 32, width: 'auto' }} />
          </a>
          <button
            style={{ ...common.btn, ...common.btnGhost }}
            onClick={() => navigate(`/projects/${projectId}`)}
          >
            ← Back
          </button>
          <h1 style={common.appTitle}>Hotspot Editor</h1>
        </div>
        <p style={s.hint}>
          Draw a new hotspot by dragging on the image &nbsp;·&nbsp; Drag an
          existing hotspot to move it &nbsp;·&nbsp; Drag its edges to resize it
        </p>
      </header>

      <main id="main-content" style={s.main}>
        {/* ── Sidebar ── */}
        <aside style={s.sidebar} aria-label="Screens">
          <h2 style={s.sidebarTitle}>Screens</h2>
          <label
            style={{ ...common.btn, ...common.btnPrimary, ...s.sidebarBtn }}
          >
            + Upload
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleSidebarUpload}
              style={{ display: "none" }}
            />
          </label>
          <button
            style={{ ...common.btn, ...common.btnSecondary, ...s.sidebarBtn }}
            onClick={handleSidebarPaste}
          >
            📋 Paste
          </button>
          {screens.map((screen, index) => (
            <div
              key={screen.id}
              role="button"
              tabIndex={0}
              aria-label={`Go to screen ${index + 1}`}
              aria-current={screen.id === parseInt(screenId) ? 'true' : undefined}
              style={{
                ...s.screenThumb,
                border:
                  screen.id === parseInt(screenId)
                    ? `2px solid ${colors.indigo}`
                    : "2px solid transparent",
              }}
              onClick={() =>
                navigate(`/projects/${projectId}/edit/${screen.id}`)
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(`/projects/${projectId}/edit/${screen.id}`);
                }
              }}
            >
              <img
                src={`${BASE_URL}/uploads/${screen.image_path}`}
                alt={`Screen ${index + 1}`}
                style={s.thumbImage}
              />
              <span style={s.thumbLabel}>Screen {index + 1}</span>
            </div>
          ))}
        </aside>

        {/* ── Canvas ── */}
        <div style={s.canvas} onMouseDown={handleBlankClick}>
          <div style={s.imageContainer}>
            <img
              ref={imageRef}
              src={
                currentScreen
                  ? `${BASE_URL}/uploads/${currentScreen.image_path}`
                  : ""
              }
              alt="Screen"
              style={s.image}
              onMouseDown={handleImageMouseDown}
              draggable={false}
            />

            {hotspots.map((hotspot, index) => {
              const isSelected = selectedHotspot?.id === hotspot.id;
              return (
                <div
                  key={hotspot.id}
                  style={{
                    ...s.hotspot,
                    ...getScaledStyle(hotspot),
                    borderRadius: isSelected
                      ? shapeBorderRadius(hotspotShape, roundedRadius)
                      : shapeBorderRadius(hotspot.shape, hotspot.border_radius ?? 12),
                    border: isSelected
                      ? `2px solid ${colors.amber}`
                      : `2px solid ${colors.indigo}`,
                    backgroundColor: isSelected
                      ? "rgba(245,158,11,0.2)"
                      : "rgba(79,70,229,0.2)",
                    cursor: "move",
                  }}
                  onMouseDown={(e) => handleHotspotMouseDown(e, hotspot)}
                >
                  {/* Order badge */}
                  <div
                    style={{
                      position: "absolute",
                      top: -10,
                      left: -10,
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      backgroundColor: hotspot.requires_previous
                        ? colors.amber
                        : colors.indigo,
                      color: colors.white,
                      fontSize: 11,
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.4)",
                      pointerEvents: "none",
                    }}
                    title="Step number (same number = alternatives)"
                  >
                    {hotspot.step_number ?? index + 1}
                  </div>
                  {hotspot.right_click && (
                    <div
                      style={{
                        position: "absolute",
                        top: -10,
                        right: -10,
                        fontSize: 12,
                        pointerEvents: "none",
                      }}
                      title="Requires right-click"
                    >
                      🖱️
                    </div>
                  )}
                  {hotspot.requires_previous && (
                    <div
                      style={{
                        position: "absolute",
                        bottom: -10,
                        right: -10,
                        fontSize: 13,
                        pointerEvents: "none",
                      }}
                    >
                      🔒
                    </div>
                  )}
                  {isSelected &&
                    HANDLES.map((handle) => (
                      <div
                        key={handle}
                        style={{
                          ...getHandleStyle(handle),
                          cursor: handleCursors[handle],
                        }}
                        onMouseDown={(e) =>
                          handleResizeMouseDown(e, hotspot, handle)
                        }
                      />
                    ))}
                </div>
              );
            })}

            {currentRect && (
              <div
                style={{
                  ...s.hotspot,
                  ...getCurrentRectStyle(),
                  border: `2px dashed ${colors.indigo}`,
                  backgroundColor: "rgba(79,70,229,0.1)",
                }}
              />
            )}
          </div>
        </div>

        {/* ── Settings panel ── */}
        {/* Floating paste button — visible whenever clipboard has a hotspot */}
        {clipboardHotspot && !showPanel && (
          <div style={{
            position: "absolute", bottom: 20, right: 20, zIndex: 20,
          }}>
            <button
              style={{ ...common.btn, ...common.btnSecondary, gap: 6 }}
              onClick={handlePasteHotspot}
              title="Paste hotspot (Ctrl+V)"
            >
              📋 Paste Hotspot
            </button>
          </div>
        )}

        {showPanel && selectedHotspot && (
          <aside style={s.panel} aria-label="Hotspot Settings">
            <h2 style={s.panelTitle}>Hotspot Settings</h2>
            <div style={s.orderBadge}>
              Hotspot {getHotspotOrder(selectedHotspot)} of {hotspots.length}
            </div>

            {clipboardHotspot && (
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                backgroundColor: "rgba(99,102,241,0.08)",
                border: "1px solid rgba(99,102,241,0.25)",
                borderRadius: 6, padding: "6px 10px", marginBottom: 12,
              }}>
                <span style={{ fontSize: 11, color: "#a5b4fc" }}>📋 Clipboard has a hotspot</span>
                <button
                  style={{ fontSize: 11, color: "#a5b4fc", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", padding: 0 }}
                  onClick={handlePasteHotspot}
                >
                  Paste (Ctrl+V)
                </button>
              </div>
            )}

            <div style={s.coordsRow}>
              <span style={s.coordChip}>X {selectedHotspot.x}</span>
              <span style={s.coordChip}>Y {selectedHotspot.y}</span>
              <span style={s.coordChip}>W {selectedHotspot.width}</span>
              <span style={s.coordChip}>H {selectedHotspot.height}</span>
            </div>

            <label htmlFor="hotspot-type" style={common.label}>Hotspot Type</label>
            <select
              id="hotspot-type"
              style={s.select}
              value={hotspotType}
              onChange={(e) => setHotspotType(e.target.value)}
            >
              <option value="click">Click</option>
              <option value="text">Text Input</option>
              <option value="number">Number Input</option>
            </select>

            <label style={common.label}>Shape</label>
            <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
              {[
                { value: "rectangle", label: "Rectangle", radius: 2 },
                { value: "rounded", label: "Rounded", radius: 10 },
                { value: "circle", label: "Circle", radius: "50%" },
              ].map(({ value, label, radius }) => {
                const active = hotspotShape === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setHotspotShape(value)}
                    style={{
                      flex: 1, padding: "8px 4px", cursor: "pointer",
                      border: `2px solid ${active ? colors.indigo : colors.borderColor}`,
                      borderRadius: 6,
                      backgroundColor: active ? colors.indigoSubtle : colors.inputBg,
                      color: active ? "#a5b4fc" : colors.textMuted,
                      fontSize: 10, fontWeight: 700,
                      display: "flex", flexDirection: "column", alignItems: "center", gap: 5,
                      fontFamily: "inherit",
                    }}
                    aria-pressed={active}
                  >
                    <div style={{
                      width: 26, height: 16,
                      border: `2px solid ${active ? colors.indigo : colors.textMuted}`,
                      borderRadius: radius,
                      backgroundColor: active ? "rgba(99,102,241,0.2)" : "transparent",
                    }} />
                    {label}
                  </button>
                );
              })}
            </div>

            {hotspotShape === "rounded" && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <label style={common.label}>Corner Radius</label>
                  <span style={{ fontSize: 12, color: colors.textMuted, fontFamily: "monospace" }}>
                    {roundedRadius}px
                  </span>
                </div>
                <input
                  type="range"
                  min={2}
                  max={60}
                  value={roundedRadius}
                  onChange={(e) => setRoundedRadius(Number(e.target.value))}
                  style={{ width: "100%", accentColor: colors.indigo, cursor: "pointer" }}
                  aria-label="Corner radius"
                />
              </div>
            )}

            {(hotspotType === "text" || hotspotType === "number") && (
              <>
                <label htmlFor="correct-answer" style={common.label}>Correct Answer</label>
                <input
                  id="correct-answer"
                  style={s.panelInput}
                  value={correctAnswer}
                  onChange={(e) => setCorrectAnswer(e.target.value)}
                  placeholder={
                    hotspotType === "number" ? "e.g. 1920" : "e.g. MyDocument"
                  }
                  type={hotspotType === "number" ? "number" : "text"}
                />
              </>
            )}

            <label htmlFor="tooltip-text" style={common.label}>Tooltip Text</label>
            <textarea
              id="tooltip-text"
              style={s.panelTextarea}
              value={tooltipText}
              onChange={(e) => setTooltipText(e.target.value)}
              placeholder="What should the learner do here?"
              rows={3}
            />

            <label htmlFor="success-message" style={common.label}>Success Message</label>
            <input
              id="success-message"
              style={s.panelInput}
              value={successMessage}
              onChange={(e) => setSuccessMessage(e.target.value)}
              placeholder="Great job!"
            />

            <label htmlFor="fail-message" style={common.label}>Incorrect Message</label>
            <input
              id="fail-message"
              style={s.panelInput}
              value={failMessage}
              onChange={(e) => setFailMessage(e.target.value)}
              placeholder="Try again!"
            />

            <label htmlFor="step-number" style={common.label}>Step number</label>
            <input
              id="step-number"
              type="number"
              min={1}
              style={s.panelInput}
              value={stepNumber}
              onChange={(e) => setStepNumber(Math.max(1, Number(e.target.value) || 1))}
            />
            <span style={{ ...s.checkboxHint, display: 'block', marginTop: -8, marginBottom: 14 }}>
              Give two hotspots the same step number to make them alternatives —
              completing either one satisfies that step.
            </span>

            <div style={s.checkboxRow}>
              <input
                type="checkbox"
                id="right_click"
                checked={rightClick}
                onChange={(e) => setRightClick(e.target.checked)}
                style={s.checkbox}
              />
              <label htmlFor="right_click" style={s.checkboxLabel}>
                🖱️ Requires right-click
                <span style={s.checkboxHint}>
                  Learner must right-click this hotspot (the browser menu is
                  disabled inside the simulation)
                </span>
              </label>
            </div>

            {stepNumber > 1 && (
              <div style={s.checkboxRow}>
                <input
                  type="checkbox"
                  id="requires_previous"
                  checked={requiresPrevious}
                  onChange={(e) => setRequiresPrevious(e.target.checked)}
                  style={s.checkbox}
                />
                <label htmlFor="requires_previous" style={s.checkboxLabel}>
                  🔒 Requires previous steps first
                  <span style={s.checkboxHint}>
                    Learner must complete all earlier steps before this one
                    becomes active
                  </span>
                </label>
              </div>
            )}

            <p style={{ fontSize: 12, color: colors.textMuted, textAlign: "center", margin: "0 0 8px" }}>
              Changes save automatically
            </p>
            <button
              style={{
                ...common.btn,
                ...common.btnPrimary,
                width: "100%",
                justifyContent: "center",
                marginBottom: 8,
              }}
              onClick={handleSaveHotspot}
            >
              Done
            </button>
            <button
              style={{
                ...common.btn,
                ...common.btnSecondary,
                width: "100%",
                justifyContent: "center",
                marginBottom: 8,
              }}
              onClick={handleCopyHotspot}
            >
              📋 Copy Hotspot (Ctrl+C)
            </button>
            <button
              style={{
                ...common.btn,
                ...common.btnDanger,
                width: "100%",
                justifyContent: "center",
              }}
              onClick={handleDeleteHotspot}
            >
              Delete Hotspot
            </button>
          </aside>
        )}
      </main>

      {confirmDelete && (
        <ConfirmDialog
          message="Delete this hotspot? This cannot be undone."
          confirmLabel="Delete Hotspot"
          onConfirm={doDeleteHotspot}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
}
